import { initComponent } from './core/init-component';

// Switching animation (Figma "Switching grid view"): fade grids out, swap at opacity 0, fade in.
// These match the design tokens: 0.2s = --duration-fast, 0.1s = the fade-in delay (delay-100).
const FADE_MS = 200;
const FADE_IN_DELAY_MS = 100;

// Active view ("3" | "5"), SHARED across every `.js-ls-view-switch` instance (the desktop bar's
// [ 3 ]/[ 5 ] and the mobile bar's [ 2 ]/[ 1 ] are two instances of the same switch). Module scope
// = single source of truth, so they stay in sync and the choice survives the filter morph (it is
// re-applied to the `<results-list>` attribute on every reconcile, even if a morph reset it).
let currentView: string | null = null;

/**
 * PLP "View" switch. The [ 3 ]/[ 5 ] (desktop) and [ 2 ]/[ 1 ] (mobile) options carry the canonical
 * `data-view` ("3" | "5") regardless of their label, and toggle the collection between the 3-grid
 * (big cards) and 5-grid (PLP cards) by setting `data-plp-active-view` on `<results-list>` (a CSS
 * rule in `src/input.css` shows only the matching `.js-plp-view`). Switching fades the grids out,
 * swaps at opacity 0, then fades in.
 */
initComponent('.js-ls-view-switch', (root) => {
  const resultsList = document.querySelector<HTMLElement>('results-list');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let busy = false;
  let transitioning = false;

  if (currentView == null) {
    currentView = resultsList?.dataset.plpActiveView ?? '3';
  }

  const reconcile = () => {
    if (busy || currentView == null) return;
    busy = true;
    try {
      // Keep the shared view on <results-list> (repairs it if a morph reset it to the default).
      if (resultsList && resultsList.dataset.plpActiveView !== currentView) {
        resultsList.dataset.plpActiveView = currentView;
      }
      // Keep this instance's option highlight in sync (the morph re-renders the buttons).
      root.querySelectorAll<HTMLButtonElement>('.ls-view-switch__option').forEach((option) => {
        const pressed = String(option.dataset.view === currentView);
        if (option.getAttribute('aria-pressed') !== pressed) {
          option.setAttribute('aria-pressed', pressed);
        }
      });
    } finally {
      busy = false;
    }
  };

  reconcile();

  const switchView = (view: string) => {
    if (view === currentView) return;
    const views = document.querySelector<HTMLElement>('.js-plp-views');
    if (!views || prefersReducedMotion) {
      currentView = view;
      reconcile();
      return;
    }
    if (transitioning) return;
    transitioning = true;

    views.dataset.viewTransition = 'out'; // Step 1: fade out
    window.setTimeout(() => {
      currentView = view; // Step 3: number switches (instant) + view swap at opacity 0
      reconcile();
      views.dataset.viewTransition = 'in'; // Step 2: fade in (with 0.1s delay via CSS)
      window.setTimeout(() => {
        delete views.dataset.viewTransition;
        transitioning = false;
      }, FADE_IN_DELAY_MS + FADE_MS);
    }, FADE_MS);
  };

  const onClick = (event: Event) => {
    const option = (event.target as HTMLElement).closest<HTMLButtonElement>('.ls-view-switch__option');
    if (!option?.dataset.view) return;
    switchView(option.dataset.view);
  };

  root.addEventListener('click', onClick);

  // Re-apply the shared view after every results/filter re-render.
  const observer = new MutationObserver(() => reconcile());
  observer.observe(resultsList ?? document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-plp-active-view', 'aria-pressed'],
  });

  return {
    unload: () => {
      root.removeEventListener('click', onClick);
      observer.disconnect();
    },
  };
});
